import I18nHelper, { type Language } from "@helpers/I18nHelper";
import useLanguageStore from "@store/language.store";
import useLanguageSwitcherStore from "@store/languageSwitcher.store";
import { useTranslation } from "react-i18next";
import { useMatches, useNavigate, useParams } from "react-router";

const useAppLanguage = () => {
  const { browserLanguage, i18nLanguage, routeLanguage } = useLang();
  const { changeLanguage } = useChangeLang();

  const locationUrlBuilder = (...uri: string[]) => {
    const path = uri.join("/");
    if (/^https?:\/\//i.test(path)) {
      // If the built URL is absolute (external), return it unchanged.
      return path;
    }

    return I18nHelper.getLangHref(uri.join("/"), i18nLanguage);
  };

  return {
    lang: {
      browserLanguage,
      i18nLanguage,
      routeLanguage,
      // rename for clarity, but its the same as i18nLang
      currentLanguage: i18nLanguage,
    },
    changeLanguage,
    locationUrlBuilder,
  };
};

export default useAppLanguage;

const useLang = () => {
  const { i18n } = useTranslation();

  const detect = i18n.services.languageDetector?.detect() as Language | null;
  // detect() can return string | string[]
  const rawBrowserLang = Array.isArray(detect) ? detect[0] : detect;

  // If there is no detected language, fallback to i18n.language
  const browserLanguage = (rawBrowserLang ?? i18n.language).split(
    "-",
  )[0] as Language;
  const i18nLanguage = i18n.language as Language;
  const routeLanguage = I18nHelper.getLangFromPath(window.location.pathname);

  return {
    browserLanguage,
    i18nLanguage,
    routeLanguage,
  };
};

const useChangeLang = () => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const { selectLanguage } = useLanguageStore();

  const matches = useMatches();
  const params = useParams();

  const changeLanguage = (lang: Language) => {
    // 1. Find the current leaf route match that has a handle with a routeId
    const currentMatch = [...matches]
      .reverse()
      .find((m) => (m.handle as any)?.routeId);

    if (currentMatch) {
      const routeId = (currentMatch.handle as any)?.routeId;

      // 2. Get the translated path for the new language
      // We pass params (like :slug) so i18n can interpolate them
      const updatedParams = useLanguageSwitcherStore
        .getState()
        .getTranslatedParams(params, lang);

      // First try to find the route in the public routes, if not found, try the private routes
      let newPath = i18n.t(`routes.public.${routeId}`, {
        lng: lang,
        ...updatedParams,
        // return false if the key is missing, so we can detect it
        defaultValue: false,
      });

      if (!newPath) {
        newPath = i18n.t(`routes.private.${routeId}`, {
          lng: lang,
          ...updatedParams,
          // return false if the key is missing, so we can detect it
          defaultValue: false,
        });
      }

      // 3. Change language and navigate
      selectLanguage(lang);
      // If the translation failed (returned the key), fallback to home
      if (!newPath) {
        const fallbackPath = i18n.t("routes.public.home", { lng: lang });
        navigate(fallbackPath);
      } else {
        navigate(newPath);
      }
    }
  };

  return { changeLanguage };
};
